import React, { memo, useEffect } from 'react'

import {
  Notice,
  Icon,
  RotatingSpinner,
  CircleX,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { dismissExportNotification } from 'Pods/ThreeDStudio/Store/actions/export'
import { EXPORT_STATUS, isExportOwnedByCurrentView } from 'Pods/ThreeDStudio/Store/slices/export'

const AUTO_HIDE_MS = 15000

const buildIcon = (svg, color) => (
  <Icon svgComponent={svg} color={color} notSpaced size={iconSizeMedium} />
)

const VARIANT_BY_STATUS = {
  [EXPORT_STATUS.QUEUED]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    titleKey: 'shared.exportNotification.queued.title',
    descriptionKey: 'shared.exportNotification.queued.description',
    dismissible: false,
    autoHide: false,
  },
  [EXPORT_STATUS.PREPARING]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    titleKey: 'shared.exportNotification.preparing.title',
    descriptionKey: null,
    dismissible: false,
    autoHide: false,
  },
  [EXPORT_STATUS.ERROR]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    titleKey: 'shared.exportNotification.error.title',
    descriptionKey: 'shared.exportNotification.error.description',
    dismissible: true,
    autoHide: true,
  },
}

function ExportNotification() {
  const t = useTranslation()
  const status = useThreeDStudioStore(state => state.export.status)
  const isOwnedByCurrentView = useThreeDStudioStore(isExportOwnedByCurrentView)
  const variant = VARIANT_BY_STATUS[status]

  const shouldRender = !!variant && isOwnedByCurrentView

  useEffect(() => {
    if (!shouldRender || !variant?.autoHide) return undefined
    const timer = setTimeout(dismissExportNotification, AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [shouldRender, status, variant])

  if (!shouldRender) return null

  const title = t(variant.titleKey)
  const description = variant.descriptionKey ? t(variant.descriptionKey) : null

  return (
    <div
      className={classnames('export-notification', description && 'export-notification--with-description')}
      role="status"
      aria-live="polite"
    >
      <Notice
        type={variant.type}
        useSSRIcon
        ssrIcon={variant.icon}
        onClose={variant.dismissible ? dismissExportNotification : null}
      >
        <div className="export-notification__body">
          <span className="cgt-heading-50">{title}</span>
          {description && <span className="cgt-body-base">{description}</span>}
        </div>
      </Notice>
    </div>
  )
}

export default memo(ExportNotification)
